import React, { useMemo } from 'react';
import styled from 'styled-components';
import { ReactComponent as DisabledSyncIcon } from '@elementary/common-ui/assets/disabled-sync.svg';
import { ReactComponent as EditIcon } from '@elementary/common-ui/assets/edit-2.svg';
import { ReactComponent as ScheduleIcon } from '@elementary/common-ui/assets/schedule-icon.svg';
import { ReactComponent as SyncIcon } from '@elementary/common-ui/assets/sync-arrows.svg';
import { ReactComponent as SyncNowIcon } from '@elementary/common-ui/assets/sync.svg';
import { ReactComponent as WebhookIcon } from '@elementary/common-ui/assets/webhook-icon.svg';
import {
  AccordionSummary,
  Chip,
  Flex,
  FlexCenter,
  FlexColumn,
  FlexSpace,
  IconButton,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import { capitalize } from '@elementary/common-ui/utils/common';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import { getWarehouseIcon } from '@elementary/common-ui/utils/environments';
import { AccountEnvironment } from '@elementary/common-ui/types';
import { CircularProgress, Tooltip, Typography } from '@mui/material';
import cronParser from 'cron-parser';
import useInitializingEnvs from 'shared/hooks/useInitializingEnvs';

const Divider = styled.div`
  background: ${({ theme }) => theme.palette.common.gray14};
  height: 40px;
  width: 1px;
  min-width: 1px;
`;

const SyncIconStyled = styled(SyncIcon)<{ $animate?: boolean }>`
  ${({ $animate }) => $animate && 'rotateAnimation 1.8s infinite linear'};
  animation-direction: reverse;
  @keyframes rotateAnimation {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(359deg);
    }
  }
`;

const UpdatingLabel = styled(Typography)`
  font-size: 10px;
  color: ${({ theme }) => theme.palette.secondary.main};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  letter-spacing: 1px;
`;

const StatusSection = styled(FlexColumn)`
  justify-content: center;
  align-items: flex-end;
  height: 100%;
  gap: 8px;
`;

type Props = {
  onDwhDrawerOpen: () => void;
  onEnvScheduleDrawerOpen: () => void;
  onSyncNow: () => void;
  envData: AccountEnvironment;
  isSyncEnabled: boolean;
};

const IntegrationAccordionSummary = ({
  onDwhDrawerOpen,
  onEnvScheduleDrawerOpen,
  onSyncNow,
  isSyncEnabled,
  envData,
}: Props) => {
  const { isEnvInitializing } = useInitializingEnvs();

  const cronExpression = envData?.cron;
  const nextAtLabel = useMemo(
    () =>
      cronExpression
        ? `Next ${localizeDate(
            cronParser.parseExpression(cronExpression).next().toDate(),
          ).fromNow()}`
        : 'Not scheduled',
    [cronExpression],
  );

  const updatedAtLabel = useMemo(
    () => `Updated ${localizeDate(envData?.lastSyncedAt).fromNow()}`,
    [envData?.lastSyncedAt],
  );

  if (!envData) return <div />;
  const syncNowToolTipTitle = isSyncEnabled
    ? 'Sync now'
    : 'Syncing already in progress';

  return (
    <AccordionSummary
      sx={{ '.MuiAccordionSummary-content': { width: '100%' } }}
    >
      <FlexSpace sx={{ width: '100%', gap: 3 }}>
        <FlexSpace sx={{ width: '100%', overflow: 'hidden', gap: 1 }}>
          <Flex sx={{ gap: 3, alignItems: 'center', overflow: 'hidden' }}>
            <FlexCenter sx={{ svg: { width: '40px' } }}>
              {React.createElement(
                getWarehouseIcon(envData.dwhConnection.warehouseType),
              )}
            </FlexCenter>
            <FlexColumn
              sx={{
                overflow: 'hidden',
                gap: 0.5,
                justifyContent: 'center',
              }}
            >
              <Flex sx={{ gap: 1, alignItems: 'center' }}>
                <OverflowTooltip
                  bold
                  sx={{
                    fontWeight: 'bold',
                    lineHeight: '20px',
                  }}
                >
                  {capitalize(envData.name)}
                </OverflowTooltip>
                {envData?.environmentType === 'prod' && (
                  <Chip
                    sx={{ flexShrink: 0 }}
                    label={capitalize(envData?.environmentType)}
                    size="small"
                    color="primary"
                    overflow="visible"
                    bold
                  />
                )}
              </Flex>

              <Typography sx={{ fontSize: '12px' }}>
                {capitalize(envData.dwhConnection.warehouseType)}
              </Typography>
            </FlexColumn>
          </Flex>

          <FlexColumn
            sx={{
              justifyContent: 'space-between',
              gap: 1,
              height: '100%',
            }}
          >
            {!isEnvInitializing(envData.id) ? (
              <StatusSection>
                {envData.lastSyncedAt && (
                  <Typography noWrap sx={{ fontSize: '12px' }}>
                    {updatedAtLabel}
                  </Typography>
                )}

                {envData.isDeactivated ? (
                  <Typography
                    noWrap
                    sx={{
                      fontSize: '12px',
                      color: 'common.red100',
                    }}
                  >
                    Updates are deactivated
                  </Typography>
                ) : (
                  <Flex sx={{ alignItems: 'center', gap: '6px' }}>
                    {envData.lastSyncStatus !== 'running' &&
                      (envData.webhookSetup ? (
                        <WebhookIcon />
                      ) : (
                        <SyncIconStyled />
                      ))}

                    {envData.lastSyncStatus === 'running' ? (
                      <UpdatingLabel noWrap>UPDATING...</UpdatingLabel>
                    ) : (
                      <Typography
                        noWrap
                        sx={{
                          fontSize: '12px',
                          color: 'common.gray2',
                        }}
                      >
                        {envData.webhookSetup
                          ? 'Webhook triggered'
                          : nextAtLabel}
                      </Typography>
                    )}
                  </Flex>
                )}
              </StatusSection>
            ) : (
              <Flex sx={{ alignItems: 'center', gap: '6px', height: '100%' }}>
                <CircularProgress
                  size={8}
                  thickness={6}
                  sx={{ color: 'common.green100' }}
                />
                <Typography
                  noWrap
                  sx={{
                    fontSize: '12px',
                    color: 'common.green100',
                    fontWeight: 'bold',
                  }}
                >
                  Initializing...
                </Typography>
              </Flex>
            )}
          </FlexColumn>
        </FlexSpace>

        <Divider />

        <Flex>
          <Tooltip title={syncNowToolTipTitle}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                if (isSyncEnabled) {
                  onSyncNow();
                }
              }}
            >
              {isSyncEnabled ? <SyncNowIcon /> : <DisabledSyncIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Schedule settings">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onEnvScheduleDrawerOpen();
              }}
            >
              <ScheduleIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit environment">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onDwhDrawerOpen();
              }}
            >
              <EditIcon width={24} />
            </IconButton>
          </Tooltip>
        </Flex>
      </FlexSpace>
    </AccordionSummary>
  );
};

export default IntegrationAccordionSummary;
